import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { LegalIntro, LegalPage, LegalSection, legalLinkClass } from "../components/legal/LegalPage";

// Ported from forzabuilt-website's src/pages/cookie-policy.astro. Keep the technologies listed
// here in sync with CATEGORIES in components/consent/CookieConsentManager.jsx.
const CookiePolicy = () => (
  <LegalPage title="Cookie Policy" effectiveDate="October 16, 2026">
    <Helmet>
      <title>Cookie Policy | RuggedRed</title>
      <meta
        name="description"
        content="How RuggedRed uses cookies and similar technologies on this site, and how to manage preferences, including Global Privacy Control."
      />
      <link rel="canonical" href="https://www.ruggedred.com/cookie-policy" />
    </Helmet>

    <LegalIntro>
      <p>
        This Cookie Policy explains how RuggedRed uses cookies and similar technologies on this
        website and your available controls.
      </p>
    </LegalIntro>

    <LegalSection title="Types of Technologies We Use">
      <p>
        <strong>Cookies:</strong> Small text files stored in your browser to remember settings and
        interactions, as well as similar technologies such as tags, pixels, scripts, and browser
        storage that help operate the site and measure site usage.
      </p>
    </LegalSection>

    <LegalSection title="Cookie Categories and Technologies">
      <p>
        <strong>Essential (always active):</strong> Required for basic website functionality and
        security. These include first-party storage that remembers your cookie preferences on this
        browser and settings that support core site features. They cannot be switched off.
      </p>
      <p>
        <strong>Analytics &amp; Customization:</strong> Help us count visits, understand how
        visitors move around the website, and improve performance and user experience.
        Technologies in this category: <strong>Vercel Analytics</strong> (cookieless page and event
        measurement provided by our hosting platform). If you opt out of this category, analytics
        collection stops.
      </p>
      <p>
        <strong>Advertising:</strong> Used by us and third parties who partner with us to support
        marketing measurement and business audience insights. We do not currently use any
        advertising technologies on this website. If we add any, they will be listed here and in
        Cookie Preferences, and your choice for this category will apply to them. Depending on your
        state's law, such activity may be considered a "sale" or "sharing" of personal information,
        and you have the right to opt out.
      </p>
    </LegalSection>

    <LegalSection title="How Consent Works on This Site">
      <p>
        Consistent with applicable U.S. state privacy laws, non-essential technologies operate by
        default and you may <strong>opt out at any time</strong>. When you first visit, a banner
        lets you accept, reject non-essential technologies, or set category-level preferences. Your
        choice is saved in this browser and applied on every page. You can change it at any time
        using the "Cookie Preferences" link in the website footer.
      </p>
      <p>
        When you make a choice, we keep an <strong>anonymous record</strong> of it (a random
        identifier, your selections, the date, and this policy's version) for compliance and audit
        purposes. This record contains no name, email, or IP address and is not linked to any
        account.
      </p>
      <p>
        <strong>Global Privacy Control (GPC):</strong> If your browser sends a GPC signal, we treat
        it as a valid opt-out of the Advertising category automatically — no further action is
        needed. You may still enable that category manually through Cookie Preferences if you wish.
      </p>
    </LegalSection>

    <LegalSection title="Managing Your Preferences">
      <p>
        You have the right to opt out of the use, sale, or sharing of your personal information. You
        can do this at any time using the "Cookie Preferences" link in the website footer, by
        emailing us at{" "}
        <a href="mailto:info@ruggedred.com" className={legalLinkClass}>
          info@ruggedred.com
        </a>
        , or by enabling Global Privacy Control (GPC) in a supporting browser. For applicable
        requests, you may also use our{" "}
        <Link to="/do-not-sell-or-share" className={legalLinkClass}>
          Do Not Sell or Share My Personal Information
        </Link>{" "}
        page. Your preferences apply to this browser on this device; clearing site data may reset
        them. Blocking some technologies may affect website functionality.
      </p>
    </LegalSection>

    <LegalSection title="Changes to This Policy">
      <p>
        We may update this Cookie Policy from time to time. The updated version will be posted on
        this page with a revised effective date.
      </p>
    </LegalSection>
  </LegalPage>
);

export default CookiePolicy;
